import { useAppStore } from '../store/useAppStore'
import { ChevronRight, User, Flame, X, Plus, Star } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import { useState, useEffect } from 'react'
import OnboardingModal from '../components/OnboardingModal'

export const STREAK_BADGES = [
  { code: 'streak_3',  emoji: '🌱', label: 'เริ่มต้นดี',   threshold: 3,  bonus: 1 },
  { code: 'streak_7',  emoji: '⭐', label: 'สม่ำเสมอ',     threshold: 7,  bonus: 2 },
  { code: 'streak_14', emoji: '💎', label: 'ผู้ภักดี',     threshold: 14, bonus: 3 },
  { code: 'streak_30', emoji: '👑', label: 'ราชา Streak', threshold: 30, bonus: 5 },
] as const

function nextTier(streak: number): { threshold: number; emoji: string; label: string } {
  for (const b of STREAK_BADGES) {
    if (streak < b.threshold) return { threshold: b.threshold, emoji: b.emoji, label: b.label }
  }
  // ครบ 30 แล้ว → cycle ทุก 30
  const next = Math.ceil((streak + 1) / 30) * 30
  return { threshold: next, emoji: '👑', label: 'ราชา Streak' }
}

interface LotteryLatest {
  draw_date: string
  first_prize: number | null
  two_digit: number | null
  three_digit_front: number[] | null
  three_digit_last: number[] | null
}

const THAI_MONTHS_FULL = ['มกราคม','กุมภาพันธ์','มีนาคม','เมษายน','พฤษภาคม','มิถุนายน','กรกฎาคม','สิงหาคม','กันยายน','ตุลาคม','พฤศจิกายน','ธันวาคม']

function formatThaiDateFull(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number)
  return `${d} ${THAI_MONTHS_FULL[m - 1]} ${y + 543}`
}

/** เลขฮิตวันนี้ — social proof + tap to save (viral loop) */
function PopularNumbersCard() {
  const { lineUid } = useAppStore()
  const queryClient = useQueryClient()
  const [savingNum, setSavingNum] = useState<string | null>(null)
  const [toast, setToast] = useState<string | null>(null)

  const { data: popular } = useQuery({
    queryKey: ['number', 'popular'],
    queryFn: () => api.number.popular(5),
    staleTime: 1000 * 60 * 5,
  })

  const { data: saved } = useQuery({
    queryKey: ['number', 'saved', lineUid],
    queryFn: () => api.number.list(lineUid!),
    enabled: !!lineUid,
    staleTime: 0,
  })

  if (!popular || popular.length === 0) return null

  const savedSet = new Set((saved ?? []).map(s => s.number))
  const slotsUsed = (saved ?? []).length

  async function handleSave(num: string) {
    if (!lineUid || savingNum) return
    if (savedSet.has(num)) return
    if (slotsUsed >= 5) {
      setToast('เลขเก็บเต็มแล้ว (5 เลข)')
      setTimeout(() => setToast(null), 2500)
      return
    }
    setSavingNum(num)
    setToast(null)
    try {
      await api.number.save(lineUid, num)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['number', 'popular'] }),
        queryClient.invalidateQueries({ queryKey: ['number', 'saved', lineUid] }),
      ])
      setToast(`เก็บเลข ${num} แล้ว ⭐`)
    } catch (err) {
      const code = (err as Error & { code?: string }).code
      setToast(code === 'limit_reached' ? 'เลขเก็บเต็มแล้ว (5 เลข)' : 'บันทึกไม่สำเร็จ')
    } finally {
      setSavingNum(null)
      setTimeout(() => setToast(null), 2500)
    }
  }

  return (
    <div className="mx-4 mb-3 p-4 rounded-2xl card-amulet"
         style={{ border: '1px solid var(--color-gold)' }}>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <span className="text-lg">{'\u{1F525}'}</span>
          <h3 className="text-sm font-bold" style={{ color: 'var(--color-gold-dark)' }}>
            {'เลขฮิตวันนี้'}
          </h3>
        </div>
        <span className="text-[10px]" style={{ color: 'var(--color-text-soft)' }}>
          {slotsUsed}{'/5 ที่เก็บ'}
        </span>
      </div>
      <p className="text-[10px] mb-3" style={{ color: 'var(--color-text-soft)' }}>
        {'แตะ + เพื่อเก็บเลขนี้ของคุณด้วย'}
      </p>
      <div className="flex flex-wrap gap-2">
        {popular.map(item => {
          const isSaved = savedSet.has(item.number)
          const isFull  = !isSaved && slotsUsed >= 5
          const isLoading = savingNum === item.number
          return (
            <button key={item.number}
                    onClick={() => handleSave(item.number)}
                    disabled={isSaved || isFull || isLoading}
                    className="flex items-center gap-2 pl-3 pr-2 py-1.5 rounded-xl transition-transform active:scale-95 disabled:active:scale-100"
                    style={{
                      background: 'var(--gradient-gold)',
                      boxShadow: '0 2px 8px rgba(184,148,90,0.25)',
                      opacity: isFull ? 0.45 : 1,
                    }}>
              <span className="font-mono font-bold text-base text-white">{item.number}</span>
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                    style={{ background: 'rgba(255,255,255,0.25)', color: '#fff' }}>
                {item.count}{' คน'}
              </span>
              <span className="w-5 h-5 rounded-full flex items-center justify-center"
                    style={{ background: 'rgba(255,255,255,0.3)' }}>
                {isSaved
                  ? <Star size={11} fill="#fff" color="#fff" />
                  : <Plus size={12} color="#fff" strokeWidth={3} />}
              </span>
            </button>
          )
        })}
      </div>
      {toast && (
        <p className="text-center text-[11px] mt-3 font-medium"
           style={{ color: toast.startsWith('เลขเก็บเต็ม') || toast.startsWith('บันทึกไม่') ? '#e53e3e' : 'var(--color-gold-dark)' }}>
          {toast}
        </p>
      )}
    </div>
  )
}

/** สีมงคลประจำวัน — deterministic per (birth_date, today) */
function LuckyColorCard() {
  const { lineUid } = useAppStore()
  const { data } = useQuery({
    queryKey: ['lucky', 'today', lineUid],
    queryFn: () => api.lucky.today(lineUid!),
    enabled: !!lineUid,
    staleTime: 1000 * 60 * 30,
  })

  if (!data || !data.main) return null

  return (
    <div className="px-4 mb-3">
      <div className="rounded-2xl p-4 card-amulet"
           style={{ border: '1px solid var(--color-gold)' }}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-lg">🎨</span>
            <span className="text-sm font-bold" style={{ color: 'var(--color-text)' }}>
              สีมงคลวันนี้
            </span>
          </div>
          <span className="text-[10px]" style={{ color: 'var(--color-text-muted)' }}>
            คนเกิดวัน{data.birth_day} · วัน{data.today_day}
          </span>
        </div>

        <div className="flex items-stretch gap-2.5">
          {/* Main color (large) */}
          <div className="flex-1 flex flex-col items-center gap-1.5 p-2.5 rounded-xl"
               style={{ background: 'rgba(255,255,255,0.04)' }}>
            <div className="w-12 h-12 rounded-full ring-2 ring-offset-2"
                 style={{
                   background: data.main.hex,
                   boxShadow: `0 4px 14px ${data.main.hex}55`,
                 }} />
            <div className="text-[11px] font-bold" style={{ color: 'var(--color-gold)' }}>
              {data.main.name}
            </div>
            <div className="text-[10px] text-center leading-tight" style={{ color: 'var(--color-text-muted)' }}>
              {data.main.reason}
            </div>
          </div>

          {/* Accent + Taboo (small stack) */}
          <div className="flex flex-col gap-2 w-[110px]">
            <div className="flex items-center gap-2 p-2 rounded-xl"
                 style={{ background: 'rgba(255,255,255,0.04)' }}>
              <div className="w-7 h-7 rounded-full"
                   style={{ background: data.accent.hex, boxShadow: `0 2px 6px ${data.accent.hex}55` }} />
              <div className="flex-1 min-w-0">
                <div className="text-[9px]" style={{ color: 'var(--color-text-muted)' }}>เสริมรอง</div>
                <div className="text-[11px] font-bold truncate" style={{ color: 'var(--color-text)' }}>
                  {data.accent.name}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 p-2 rounded-xl"
                 style={{ background: 'rgba(229,62,62,0.06)', border: '1px solid rgba(229,62,62,0.18)' }}>
              <div className="relative w-7 h-7 rounded-full"
                   style={{
                     background: data.taboo.hex,
                     boxShadow: 'inset 0 0 0 2px rgba(229,62,62,0.9)',
                   }}>
                {/* strike-through line indicating "avoid" */}
                <span className="absolute inset-0 flex items-center justify-center">
                  <span className="block w-full h-[2px] rotate-45"
                        style={{ background: 'rgba(229,62,62,0.9)' }} />
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[9px]" style={{ color: '#e53e3e' }}>หลีกเลี่ยง</div>
                <div className="text-[11px] font-bold truncate" style={{ color: 'var(--color-text)' }}>
                  {data.taboo.name}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/** Featured Government Lottery Card — luxury sage+gold design */
function FeaturedLotteryCard() {
  const { data, isLoading } = useQuery({
    queryKey: ['lottery', 'home', 'government'],
    queryFn: () => api.lottery.latest('government') as Promise<LotteryLatest>,
    staleTime: 1000 * 60 * 10,
  })

  if (isLoading) {
    return (
      <div className="card-amulet p-6 mx-4 mb-4 animate-pulse">
        <div className="h-4 w-2/3 mb-2 rounded" style={{ background: 'var(--color-surface-2)' }} />
        <div className="h-12 mb-4 rounded" style={{ background: 'var(--color-surface-2)' }} />
        <div className="grid grid-cols-3 gap-2">
          <div className="h-16 rounded" style={{ background: 'var(--color-surface-2)' }} />
          <div className="h-16 rounded" style={{ background: 'var(--color-surface-2)' }} />
          <div className="h-16 rounded" style={{ background: 'var(--color-surface-2)' }} />
        </div>
      </div>
    )
  }

  if (!data) return null

  const front3 = data.three_digit_front ?? []
  const last3 = data.three_digit_last ?? []

  return (
    <div className="card-premium mx-4 mb-4">
      {/* Header strip */}
      <div className="px-4 py-2.5 rounded-t-[calc(1.5rem-2px)]"
           style={{ background: 'var(--gradient-gold)' }}>
        <p className="text-center text-white text-xs font-semibold tracking-wide">
          ผลสลากกินแบ่งรัฐบาล
        </p>
        <p className="text-center text-white text-[10px] opacity-90 mt-0.5">
          งวดวันที่ {formatThaiDateFull(data.draw_date)}
        </p>
      </div>

      {/* รางวัลที่ 1 — fit container */}
      <div className="px-4 pt-5 pb-4 text-center">
        <div className="text-[10px] uppercase tracking-widest mb-1.5"
             style={{ color: 'var(--color-gold-dark)' }}>
          รางวัลที่ 1
        </div>
        <div className="font-black font-mono"
             style={{
               color: 'var(--color-text)',
               fontSize: 'clamp(2rem, 11vw, 3rem)',
               letterSpacing: '0.05em',
             }}>
          {data.first_prize?.toString().padStart(6, '0') ?? '------'}
        </div>
      </div>

      {/* เลขท้าย 2 ตัว — hero card center */}
      <div className="mx-4 mb-3 py-4 px-4 rounded-2xl text-center"
           style={{
             background: 'var(--gradient-sage)',
             boxShadow: '0 4px 14px rgba(122, 138, 106, 0.25)',
           }}>
        <div className="text-[10px] uppercase tracking-widest mb-2 text-white/85">
          เลขท้าย 2 ตัว
        </div>
        <div className="font-black font-mono text-white"
             style={{
               fontSize: 'clamp(2.5rem, 13vw, 3.5rem)',
               letterSpacing: '0.15em',
               lineHeight: 1.1,
               paddingLeft: '0.15em',
             }}>
          {data.two_digit !== null && data.two_digit !== undefined
            ? String(data.two_digit).padStart(2, '0')
            : '--'}
        </div>
      </div>

      {/* เลขหน้า 3 + ท้าย 3 — secondary row */}
      <div className="grid grid-cols-2 gap-2 px-4 pb-4">
        <PrizeBox
          label="เลขหน้า 3 ตัว"
          values={front3.map(n => n.toString().padStart(3, '0'))}
        />
        <PrizeBox
          label="เลขท้าย 3 ตัว"
          values={last3.map(n => n.toString().padStart(3, '0'))}
        />
      </div>
    </div>
  )
}

function PrizeBox({ label, values }: {
  label: string
  values: string[]
}) {
  return (
    <div className="rounded-xl p-3 text-center"
         style={{
           background: 'var(--color-surface-2)',
           border: '1px solid var(--color-surface-3)',
         }}>
      <div className="text-[10px] font-semibold mb-1.5"
           style={{ color: 'var(--color-text-muted)' }}>
        {label}
      </div>
      <div className="flex items-center justify-center gap-2">
        {values.length > 0 ? values.map((v, i) => (
          <div key={i} className="font-mono font-bold text-xl"
               style={{ color: 'var(--color-text)' }}>
            {v}
          </div>
        )) : (
          <div className="font-mono font-bold text-xl"
               style={{ color: 'var(--color-text-soft)' }}>
            —
          </div>
        )}
      </div>
    </div>
  )
}

export default function HomePage() {
  const { profile, lineUid, setProfile } = useAppStore()
  const navigate = useNavigate()

  const tickets = profile?.dream_quota?.paid_tickets ?? 0

  const [checkedIn, setCheckedIn] = useState(profile?.checkin?.checked_in_today ?? false)
  const [checkingIn, setCheckingIn] = useState(false)
  const [milestonePopup, setMilestonePopup] = useState<{ tier: number; bonus: number } | null>(null)
  const [showOnboarding, setShowOnboarding] = useState(false)
  const [claimingOnboarding, setClaimingOnboarding] = useState(false)

  useEffect(() => {
    setCheckedIn(profile?.checkin?.checked_in_today ?? false)
  }, [profile?.checkin?.checked_in_today])

  // Trigger onboarding modal — แสดงครั้งเดียวจาก profile.onboarded === false
  useEffect(() => {
    if (profile && profile.onboarded === false) {
      setShowOnboarding(true)
    }
  }, [profile])

  async function handleOnboardComplete(refCode?: string) {
    if (claimingOnboarding || !lineUid) return
    setClaimingOnboarding(true)

    // Apply ref code first (ถ้ามี) — silent ถ้า fail
    if (refCode && refCode.length >= 4) {
      try {
        await api.referral.apply(lineUid, refCode)
      } catch (err) {
        const code = (err as Error & { code?: string }).code
        // already_referred (มาจาก URL แล้ว) → ok, อย่างอื่นแค่ log
        if (code !== 'already_referred') {
          console.warn('[Onboard] ref apply failed', code)
        }
      }
    }

    try {
      await api.user.onboard(lineUid)
    } catch (err) {
      // already_onboarded ก็ ignore — แค่ปิด modal
      const code = (err as Error & { code?: string }).code
      if (code !== 'already_onboarded') {
        console.error('[Onboard] failed', err)
      }
    }
    try {
      const fresh = await api.user.profile(lineUid) as Parameters<typeof setProfile>[0]
      setProfile(fresh)
    } catch {}
    setClaimingOnboarding(false)
    setShowOnboarding(false)
  }

  async function handleCheckin() {
    if (checkedIn || checkingIn || !lineUid) return
    setCheckingIn(true)
    setCheckedIn(true)
    try {
      const res = await api.user.checkin(lineUid)
      const fresh = await api.user.profile(lineUid) as Parameters<typeof setProfile>[0]
      setProfile(fresh)
      if (res.milestone_unlocked) {
        setMilestonePopup({ tier: res.milestone_unlocked.tier, bonus: res.milestone_unlocked.bonus })
      }
    } catch {
      setCheckedIn(false)
    } finally {
      setCheckingIn(false)
    }
  }

  const streakDays = profile?.streak?.days ?? 0
  const next = nextTier(streakDays)
  const prevTierThreshold = (() => {
    let prev = 0
    for (const b of STREAK_BADGES) {
      if (streakDays >= b.threshold) prev = b.threshold
    }
    // หลัง 30 → reset เริ่มจาก 30 ทบไป
    if (streakDays >= 30) prev = Math.floor(streakDays / 30) * 30
    return prev
  })()
  const progressPct = Math.max(0, Math.min(100,
    ((streakDays - prevTierThreshold) / Math.max(1, next.threshold - prevTierThreshold)) * 100
  ))

  const dreamBadge = (() => {
    if (tickets > 0) return `🎫 ${tickets} ครั้ง`
    return 'ต้องใช้ตั๋ว'
  })()

  const cards = [
    {
      icon: '🔮',
      title: 'ทำนายฝัน',
      desc: 'วิเคราะห์ตามตำราโบราณ → เลขมงคล',
      path: '/dream',
      badge: dreamBadge,
      featured: true,
    },
    {
      icon: '🃏',
      title: 'ไพ่ยิปซีวันนี้',
      desc: 'Major Arcana 22 ใบ — สุ่ม 1 ใบ/วัน',
      path: '/tarot',
      badge: '1 ตั๋ว/ใบ',
    },
    {
      icon: '🎰',
      title: 'ผลหวยย้อนหลัง',
      desc: 'ผลสลากกินแบ่งรัฐบาล 33 งวด',
      path: '/lottery',
      badge: 'อัปเดตทุกงวด',
    },
    {
      icon: '📊',
      title: 'เลขเด่นตามสถิติ',
      desc: 'เลขร้อน-เลขเย็น ย้อนหลัง 10 ปี',
      path: '/heatmap',
      badge: 'ข้อมูล 10 ปี',
    },
  ]

  return (
    <div className="flex flex-col min-h-dvh"
         style={{ background: 'var(--color-bg)' }}>
      {showOnboarding && (
        <OnboardingModal onComplete={handleOnboardComplete} isClaiming={claimingOnboarding} />
      )}

      {/* ── Header with Logo + Profile ───────────── */}
      <div className="px-4 pt-6 pb-3 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <img
            src="/logo.png"
            alt="Amulet Digit"
            className="w-11 h-11 object-cover rounded-full"
            style={{ border: '1.5px solid var(--color-gold)', boxShadow: '0 2px 8px rgba(184, 148, 90, 0.25)' }}
            onError={(e) => {
              const target = e.target as HTMLImageElement
              target.style.display = 'none'
              target.nextElementSibling?.removeAttribute('style')
            }}
          />
          <span className="text-3xl" style={{ display: 'none' }}>🪬</span>
          <div>
            <div className="text-lg font-bold leading-tight"
                 style={{ color: 'var(--color-gold-dark)' }}>
              Amulet Digit
            </div>
            <div className="text-[10px] leading-tight"
                 style={{ color: 'var(--color-text-muted)' }}>
              ศูนย์รวมความมงคล
            </div>
            {profile?.display_name && (
              <div className="text-[10px] mt-0.5" style={{ color: 'var(--color-text-soft)' }}>
                สวัสดี, {profile.display_name}
              </div>
            )}
          </div>
        </div>
        <button onClick={() => navigate('/profile')}
                className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden"
                style={{ background: 'var(--color-surface)', border: '1px solid var(--color-surface-3)' }}>
          {profile?.avatar_url ? (
            <img src={profile.avatar_url} alt="" className="w-full h-full object-cover" />
          ) : (
            <User size={18} style={{ color: 'var(--color-text-muted)' }} />
          )}
        </button>
      </div>

      {/* ── Featured Lottery Card ────────────────── */}
      <FeaturedLotteryCard />

      {/* ── สีมงคลวันนี้ 🎨 (personal) ──────────── */}
      <LuckyColorCard />

      {/* ── เลขฮิตวันนี้ 🔥 (social proof) ─────── */}
      <PopularNumbersCard />

      {/* ── ตรวจลอตเตอรี่ CTA ───────────────────── */}
      <div className="px-4 mb-3">
        <button
          onClick={() => navigate('/check')}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-left transition-transform active:scale-[0.98] card-amulet"
          style={{ borderColor: 'var(--color-gold)', border: '1px solid var(--color-gold)' }}
        >
          <span className="text-2xl">🎫</span>
          <div className="flex-1">
            <div className="text-sm font-bold" style={{ color: 'var(--color-text)' }}>ตรวจลอตเตอรี่</div>
            <div className="text-xs" style={{ color: 'var(--color-text-muted)' }}>กรอกเลข 6 หลัก ตรวจทุกรางวัลทันที</div>
          </div>
          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                style={{ background: 'rgba(184,148,90,0.12)', color: 'var(--color-gold-dark)' }}>
            ฟรี ทุกรางวัล
          </span>
        </button>
      </div>

      {/* ── Tagline ──────────────────────────────── */}
      <div className="mx-4 mb-4 py-3 px-4 rounded-2xl text-center"
           style={{ background: 'var(--gradient-gold)', boxShadow: '0 4px 16px rgba(184, 148, 90, 0.3)' }}>
        <div className="text-sm font-semibold text-white tracking-wide">
          ✨ ทำนายฝัน ค้นพบเลขมงคลของคุณ ✨
        </div>
      </div>

      {/* ── Daily Check-in + Streak ──────────────── */}
      <div className="px-4 mb-3">
        <div className={`w-full rounded-2xl overflow-hidden ${checkedIn ? '' : ''}`}
             style={{
               background: checkedIn ? 'var(--color-surface)' : 'var(--gradient-gold)',
               border: checkedIn ? '1px solid var(--color-surface-3)' : 'none',
               boxShadow: checkedIn ? 'none' : '0 4px 14px rgba(184,148,90,0.3)',
             }}>
          <button
            onClick={handleCheckin}
            disabled={checkedIn || checkingIn}
            className={`w-full p-4 flex items-center gap-4 text-left transition-all active:scale-[0.98] ${checkedIn ? '' : 'shimmer'}`}>
            <div className="w-12 h-12 flex items-center justify-center rounded-xl flex-shrink-0 relative"
                 style={{ background: checkedIn ? 'var(--color-surface-2)' : 'rgba(255,255,255,0.2)' }}>
              {streakDays > 0 ? (
                <>
                  <Flame size={26} fill={checkedIn ? 'var(--color-gold)' : '#fff'} color={checkedIn ? 'var(--color-gold-dark)' : '#fff'} />
                  <span className="absolute -bottom-0.5 -right-0.5 text-[10px] font-bold px-1 rounded"
                        style={{ background: checkedIn ? 'var(--color-gold)' : '#fff', color: checkedIn ? '#fff' : 'var(--color-gold-dark)' }}>
                    {streakDays}
                  </span>
                </>
              ) : (
                <span className="text-2xl">{checkedIn ? '✅' : '🎁'}</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm"
                   style={{ color: checkedIn ? 'var(--color-text-muted)' : '#fff' }}>
                {checkedIn
                  ? '✅ วันนี้รับไปแล้ว'
                  : (streakDays > 0 ? `ต่อ streak ${streakDays} วัน!` : 'เช็คอินรับตั๋วฟรี!')}
              </div>
              <div className="text-xs mt-0.5"
                   style={{ color: checkedIn ? 'var(--color-text-soft)' : 'rgba(255,255,255,0.85)' }}>
                {checkedIn
                  ? (streakDays > 0
                      ? `🔥 streak ${streakDays} วัน • พรุ่งนี้มาใหม่นะ 🌟`
                      : 'พรุ่งนี้มาใหม่นะ 🌟')
                  : '1 ตั๋วฟรี + ต่อ streak ลุ้นรางวัล'}
              </div>
            </div>
            {!checkedIn && <ChevronRight size={18} style={{ color: 'rgba(255,255,255,0.7)' }} />}
          </button>
          {streakDays > 0 && (
            <div className="px-4 pb-3">
              <div className="h-1.5 rounded-full overflow-hidden"
                   style={{ background: checkedIn ? 'var(--color-surface-2)' : 'rgba(255,255,255,0.25)' }}>
                <div className="h-full rounded-full transition-all"
                     style={{
                       width: `${progressPct}%`,
                       background: checkedIn ? 'var(--color-gold)' : '#fff',
                     }} />
              </div>
              <div className="flex items-center justify-between text-[10px] mt-1.5"
                   style={{ color: checkedIn ? 'var(--color-text-soft)' : 'rgba(255,255,255,0.8)' }}>
                <span>{streakDays} วัน</span>
                <span>{next.emoji} {next.threshold} วัน</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Milestone Popup */}
      {milestonePopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6"
             style={{ background: 'rgba(0,0,0,0.65)' }} onClick={() => setMilestonePopup(null)}>
          <div className="w-full max-w-sm rounded-3xl p-6 text-center relative"
               style={{ background: 'var(--gradient-gold)', boxShadow: '0 8px 32px rgba(184,148,90,0.5)' }}
               onClick={e => e.stopPropagation()}>
            <button onClick={() => setMilestonePopup(null)}
                    className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center"
                    style={{ background: 'rgba(255,255,255,0.25)' }}>
              <X size={14} color="#fff" />
            </button>
            <div className="text-7xl mb-2 mt-2">
              {STREAK_BADGES.find(b => b.threshold === milestonePopup.tier)?.emoji ?? '👑'}
            </div>
            <div className="text-xs uppercase tracking-widest text-white/80 mb-1">Milestone Unlocked</div>
            <h3 className="text-2xl font-bold text-white mb-1">
              Streak {milestonePopup.tier} วัน!
            </h3>
            <p className="text-sm text-white/90 mb-5">
              {STREAK_BADGES.find(b => b.threshold === milestonePopup.tier)?.label ?? ''}
            </p>
            <div className="rounded-2xl p-4 mb-5"
                 style={{ background: 'rgba(255,255,255,0.18)' }}>
              <div className="text-[11px] text-white/80 mb-1">รางวัลพิเศษ</div>
              <div className="text-2xl font-bold text-white">🎫 +{milestonePopup.bonus} ตั๋ว</div>
            </div>
            <button onClick={() => setMilestonePopup(null)}
                    className="w-full py-3 rounded-xl font-bold text-base"
                    style={{ background: '#fff', color: 'var(--color-gold-dark)' }}>
              เยี่ยม! รับเลย ✨
            </button>
          </div>
        </div>
      )}

      {/* ── Feature Cards ────────────────────────── */}
      <div className="px-4 flex flex-col gap-3 pb-4">
        {cards.map(card => (
          <button key={card.path} onClick={() => navigate(card.path)}
                  className={`flex items-center gap-4 p-4 rounded-2xl text-left transition-transform active:scale-[0.98] ${card.featured ? 'card-premium' : 'card-amulet'}`}>
            <div className="text-3xl w-14 h-14 flex items-center justify-center rounded-2xl"
                 style={{
                   background: card.featured ? 'var(--gradient-gold)' : 'var(--color-surface-2)',
                 }}>
              {card.icon}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-base" style={{ color: 'var(--color-text)' }}>
                {card.title}
              </div>
              <div className="text-xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>
                {card.desc}
              </div>
            </div>
            <div className="flex flex-col items-end gap-1">
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap"
                    style={{
                      background: card.featured ? 'rgba(255,255,255,0.2)' : 'var(--color-surface-2)',
                      color: card.featured ? 'var(--color-gold-dark)' : 'var(--color-text-muted)',
                    }}>
                {card.badge}
              </span>
              <ChevronRight size={16} style={{ color: 'var(--color-text-soft)' }} />
            </div>
          </button>
        ))}
      </div>

      <div className="mt-auto pt-6 pb-3 px-4">
        <p className="text-center text-[10px]" style={{ color: 'var(--color-text-soft)' }}>
          เพื่อความบันเทิงและข้อมูลเชิงสถิติเท่านั้น • ไม่ใช่คำแนะนำทางการเงิน
        </p>
      </div>
    </div>
  )
}
